import { useState, useEffect } from "react";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Save, RefreshCw } from "lucide-react";

const AdminSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [settings, setSettings] = useState({
    brand_name: "",
    hero_title: "",
    hero_subtitle: "",
    discord_url: "",
    footer_text: "",
    contact_email: "",
    stat_customers: "",
    stat_customers_label: "",
    stat_scripts: "",
    stat_scripts_label: "",
    stat_support: "",
    stat_support_label: "",
    show_stats: false
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await api.get("/settings");
        setSettings((current) => ({ ...current, ...response.data }));
      } catch (error) {
        toast.error("Failed to load settings");
      }
      setLoading(false);
    };

    fetchSettings();
  }, []);

  const handleChange = (e) => {
    setSettings({ ...settings, [e.target.name]: e.target.value });
  };

  const handleSwitchChange = (name, checked) => {
    setSettings({ ...settings, [name]: checked });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await api.put("/admin/settings", settings);
      toast.success("Settings saved");
    } catch (error) {
      toast.error("Failed to save settings");
    }

    setSaving(false);
  };

  if (loading) {
    return (
      <AdminLayout title="Site Settings">
        <div className="h-96 flex items-center justify-center">
          <div className="loading-spinner" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Site Settings">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-2xl"
      >
        <form onSubmit={handleSubmit} className="space-y-6" data-testid="settings-form">
          {/* Brand Settings */}
          <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6 space-y-4">
            <h3 className="text-sm uppercase tracking-widest text-muted-foreground mb-4">
              Brand Settings
            </h3>

            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                Brand Name
              </label>
              <Input
                name="brand_name"
                value={settings.brand_name}
                onChange={handleChange}
                data-testid="setting-brand-name"
                className="bg-slate-950/50 border-slate-800 rounded-lg"
              />
            </div>

            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                Contact Email
              </label>
              <Input
                type="email"
                name="contact_email"
                value={settings.contact_email}
                onChange={handleChange}
                data-testid="setting-contact-email"
                className="bg-slate-950/50 border-slate-800 rounded-lg"
              />
            </div>
          </div>

          {/* Hero Section */}
          <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6 space-y-4">
            <h3 className="text-sm uppercase tracking-widest text-muted-foreground mb-4">
              Homepage Hero
            </h3>

            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                Main Title
              </label>
              <Input
                name="hero_title"
                value={settings.hero_title}
                onChange={handleChange}
                data-testid="setting-hero-title"
                className="bg-slate-950/50 border-slate-800 rounded-lg"
              />
            </div>

            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                Subtitle
              </label>
              <Input
                name="hero_subtitle"
                value={settings.hero_subtitle}
                onChange={handleChange}
                data-testid="setting-hero-subtitle"
                className="bg-slate-950/50 border-slate-800 rounded-lg"
              />
            </div>
          </div>

          {/* Statistics */}
          <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6 space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm uppercase tracking-widest text-muted-foreground">
                Statistics
              </h3>
              <div className="flex items-center gap-2">
                <Label className="text-sm text-muted-foreground">Show Stats</Label>
                <Switch
                  checked={settings.show_stats}
                  onCheckedChange={(checked) => handleSwitchChange("show_stats", checked)}
                  data-testid="setting-show-stats"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                  Customers Value
                </label>
                <Input
                  name="stat_customers"
                  value={settings.stat_customers}
                  onChange={handleChange}
                  placeholder="e.g. 10+"
                  className="bg-slate-950/50 border-slate-800 rounded-lg"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                  Customers Label
                </label>
                <Input
                  name="stat_customers_label"
                  value={settings.stat_customers_label}
                  onChange={handleChange}
                  placeholder="e.g. Happy Customers"
                  className="bg-slate-950/50 border-slate-800 rounded-lg"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                  Scripts Value
                </label>
                <Input
                  name="stat_scripts"
                  value={settings.stat_scripts}
                  onChange={handleChange}
                  placeholder="e.g. 3+"
                  className="bg-slate-950/50 border-slate-800 rounded-lg"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                  Scripts Label
                </label>
                <Input
                  name="stat_scripts_label"
                  value={settings.stat_scripts_label}
                  onChange={handleChange}
                  placeholder="e.g. Premium Scripts"
                  className="bg-slate-950/50 border-slate-800 rounded-lg"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                  Support Value
                </label>
                <Input
                  name="stat_support"
                  value={settings.stat_support}
                  onChange={handleChange}
                  placeholder="e.g. 24/7"
                  className="bg-slate-950/50 border-slate-800 rounded-lg"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                  Support Label
                </label>
                <Input
                  name="stat_support_label"
                  value={settings.stat_support_label}
                  onChange={handleChange}
                  placeholder="e.g. Support"
                  className="bg-slate-950/50 border-slate-800 rounded-lg"
                />
              </div>
            </div>
          </div>

          {/* Social & Footer */}
          <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6 space-y-4">
            <h3 className="text-sm uppercase tracking-widest text-muted-foreground mb-4">
              Social & Footer
            </h3>

            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                Discord URL
              </label>
              <Input
                name="discord_url"
                value={settings.discord_url}
                onChange={handleChange}
                data-testid="setting-discord-url"
                className="bg-slate-950/50 border-slate-800 rounded-lg"
                placeholder="https://discord.gg/..."
              />
            </div>

            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                Footer Text
              </label>
              <Input
                name="footer_text"
                value={settings.footer_text}
                onChange={handleChange}
                data-testid="setting-footer-text"
                className="bg-slate-950/50 border-slate-800 rounded-lg"
              />
            </div>
          </div>

          {/* Submit */}
          <Button
            type="submit"
            disabled={saving}
            data-testid="save-settings-btn"
            className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg uppercase tracking-widest font-bold px-8 py-6 glow-blue-hover"
          >
            {saving ? (
              <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
            ) : (
              <Save className="w-5 h-5 mr-2" />
            )}
            Save Settings
          </Button>
        </form>
      </motion.div>
    </AdminLayout>
  );
};

export default AdminSettings;
